import { useState, useEffect } from 'react';

type Stats = {
  topQueries: string[];
  topActions: string[];
  topEvents: {
    search_query: number;
    ai_response: number;
    execution_click: number;
  };
  uniqueSearchSessions?: number;
  uniqueClickSessions?: number;
  conversionRate?: number;
};

export default function FunnelsModule() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    fetch(`/api/track/stats`)
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(() => setStats(null));
  }, []);

  // Define messaging variants
  let headline = "INCOME ENGINE ACTIVATION";
  let subhead = "Deploy a proven system designed to generate income within hours — not weeks.";
  let isAdapted = false;

  // Adapt based on tracked behavior
  if (stats && stats.topEvents && stats.topEvents.execution_click >= 5) {
    headline = "HIGH-CONVERSION INCOME SYSTEM";
    subhead = "This is the path users are actively using to generate income right now.";
    isAdapted = true;
  }

  // Micro-timer feel (no real timer, just alternating signal based on current minute)
  const minuteSignal = new Date().getMinutes() % 2 === 0;

  return (
    <div style={{ padding: '20px', color: 'white', maxWidth: '600px', margin: '0 auto' }}>
      <h2 style={{ color: '#00C853', fontSize: '28px', marginBottom: '10px' }}>
        {headline}
      </h2>

      {isAdapted && (
        <div style={{ 
          color: '#FFD700', 
          fontSize: '11px', 
          fontWeight: 'bold',
          marginBottom: '8px',
          letterSpacing: '0.5px'
        }}>
          ⚡ Optimized based on live user activity
        </div>
      )}

      <p style={{ 
        color: '#CCCCCC', 
        fontSize: '16px', 
        lineHeight: '1.5',
        marginBottom: '20px'
      }}>
        {subhead}
      </p>

      {/* Trust Signal: User Count */}
      {stats && stats.topEvents && stats.topEvents.execution_click > 0 && (
        <div style={{
          marginTop: '10px',
          fontSize: '13px',
          color: '#00E5FF'
        }}>
          {stats.topEvents.execution_click} users have activated this system
        </div>
      )}

      {/* Real-Time Activity Signal */}
      {stats && stats.topEvents && stats.topEvents.execution_click >= 3 && (
        <div style={{
          marginTop: '6px',
          fontSize: '12px',
          color: '#FFD700'
        }}>
          ⚡ Activity increasing — users are actively launching now
        </div>
      )}

      {/* Trending Search Signal */}
      {stats && stats.topQueries && stats.topQueries.some((q: string) => 
        q.includes('income') || q.includes('money') || q.includes('passive')
      ) && (
        <div style={{
          marginTop: '8px',
          fontSize: '12px',
          color: '#00C853',
          marginBottom: '15px'
        }}>
          📈 Most requested: income generation systems
        </div>
      )}

      {/* Momentum Signal: Users Activating Now */}
      {stats && stats.topEvents && stats.topEvents.execution_click >= 3 && (
        <div style={{
          marginTop: '10px',
          fontSize: '13px',
          color: '#FFD700',
          fontWeight: 'bold'
        }}>
          ⚡ Users are activating this system right now
        </div>
      )}

      {/* Micro-Timer Activity Signal */}
      {minuteSignal && stats && stats.topEvents && stats.topEvents.execution_click >= 3 && (
        <div style={{
          marginTop: '6px',
          fontSize: '12px',
          color: '#FF6B00'
        }}>
          ⚡ Increased activity detected
        </div>
      )}

      <ul style={{ 
        color: '#FFFFFF', 
        fontSize: '14px', 
        lineHeight: '1.8',
        marginBottom: '30px',
        listStyle: 'none',
        paddingLeft: '0'
      }}>
        <li>• No experience required</li>
        <li>• Fast deployment (under 2 hours)</li>
        <li>• Built for immediate execution</li>
        <li>• Scalable into long-term income</li>
      </ul>

      {/* Micro Trust Line */}
      <div style={{
        marginTop: '12px',
        marginBottom: '20px',
        fontSize: '12px',
        color: '#888'
      }}>
        System optimized based on real user behavior and execution patterns
      </div>

      <div style={{ 
        color: '#00C853', 
        fontSize: '12px', 
        fontWeight: 'bold',
        letterSpacing: '1px',
        marginBottom: '8px'
      }}>
        READY TO EXECUTE:
      </div>

      {/* Button Proximity Trigger */}
      <div style={{
        marginTop: '12px',
        marginBottom: '8px',
        fontSize: '12px',
        color: '#00E5FF'
      }}>
        You're one step away from activation
      </div>

      <button
        onClick={() => {
          setActivating(true);
          setTimeout(() => {
            window.open('https://nextwealth.systeme.io/083c1601?src=nwg_cube', '_blank');
          }, 300);
        }}
        style={{
          padding: '14px 24px',
          background: '#00C853',
          border: 'none',
          borderRadius: '4px',
          color: 'black',
          fontWeight: 'bold',
          cursor: 'pointer',
          fontSize: '16px',
          width: '100%',
          transition: 'background 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#00E676';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#00C853';
        }}
      >
        🚀 Activate Income System
      </button>

      {/* Post-Click Feedback */}
      {activating && (
        <div style={{
          marginTop: '12px',
          fontSize: '13px',
          color: '#00C853',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          ✅ SYSTEM ACTIVATION STARTED...
        </div>
      )}

      <p style={{ 
        color: '#FFD700', 
        fontSize: '12px', 
        marginTop: '12px',
        textAlign: 'center'
      }}>
        ⚡ Most users begin within minutes — delay reduces momentum.
      </p>
    </div>
  );
}
